import type { ParseKeys, TFunction } from 'i18next';
import type { $ZodErrorMap, $ZodStringFormatIssues } from 'zod/v4/core';

type Primitive = string | number | symbol | bigint | boolean | null | undefined;

type FormKey = ParseKeys<'common', {}, string>;
type ErrorKey = ParseKeys<'errors', {}, 'validation'>;

type ZodI18nMapOption = {
  tForm?: TFunction<'common', string>;
  tError: TFunction<'errors', 'validation'>;
};

function stringifyPrimitive(value: unknown): string {
  if (typeof value === 'bigint') return value.toString() + 'n';
  if (typeof value === 'string') return `"${value}"`;
  return `${value}`;
}

function joinValues<T extends Primitive[]>(array: T, separator = '|'): string {
  return array.map((val) => stringifyPrimitive(val)).join(separator);
}

const parsedType = (data: unknown): string => {
  const t = typeof data;

  switch (t) {
    case 'number': {
      return Number.isNaN(data) ? 'nan' : 'number';
    }
    case 'object': {
      if (Array.isArray(data)) {
        return 'array';
      }
      if (data === null || data === undefined) {
        return 'null';
      }

      if (
        Object.getPrototypeOf(data) !== Object.prototype &&
        data.constructor
      ) {
        return data.constructor.name;
      }
    }
  }
  return t;
};

export const error: (option: ZodI18nMapOption) => $ZodErrorMap = (option) => {
  const { tForm = (k: string) => k.split('.').join(' '), tError } = option;

  return (issue) => {
    const path = tForm?.(issue?.path?.join('.') as FormKey);

    let message = tError('errors.invalid_union');

    switch (issue.code) {
      case 'invalid_type': {
        message = tError('errors.invalid_type', {
          expected: tError(`types.${issue.expected}` as ErrorKey),
          received: tError(`types.${parsedType(issue.input)}` as ErrorKey),
        });

        break;
      }

      case 'invalid_value': {
        if (issue.origin === 'string') {
          message = tError(
            `errors.invalid_string.${issue.format}` as ErrorKey,
            {
              validation: tError(`validations.${issue.format}` as ErrorKey),
              path,
            },
          ) as string;
        } else if (issue.values.length !== 1) {
          message = tError('errors.invalid_enum_value', {
            options: joinValues(issue.values, '|'),
            received: issue.input,
          });
        }

        break;
      }

      case 'too_big': {
        const maximum =
          issue.type === 'date'
            ? new Date(issue.maximum as number)
            : (issue.maximum as number);

        message = tError(
          `errors.too_big.${issue.origin}.${issue.inclusive ? 'inclusive' : 'not_inclusive'}` as ErrorKey,
          {
            maximum,
            count: typeof maximum === 'number' ? maximum : undefined,
            path,
          },
        ) as string;
        break;
      }

      case 'too_small': {
        const minimum =
          issue.type === 'date'
            ? new Date(issue.minimum as number)
            : (issue.minimum as number);

        message = tError(
          `errors.too_small.${issue.origin}.${issue.inclusive ? 'inclusive' : 'not_inclusive'}` as ErrorKey,
          {
            minimum,
            count: typeof minimum === 'number' ? minimum : undefined,
            path,
          },
        ) as string;
        break;
      }

      case 'not_multiple_of': {
        message = tError('errors.not_multiple_of', {
          multipleOf: issue.divisor as number,
          path,
        }) as string;
        break;
      }

      case 'invalid_format': {
        const _issue = issue as $ZodStringFormatIssues;
        const key = `errors.invalid_string.${_issue.format}` as ErrorKey;
        const validation = tError(`validations.${_issue.format}` as ErrorKey);

        if (_issue.format === 'starts_with') {
          message = tError('errors.invalid_string.startsWith', {
            startsWith: _issue.prefix,
            path,
          }) as string;
        } else if (_issue.format === 'ends_with') {
          message = tError('errors.invalid_string.endsWith', {
            endsWith: _issue.suffix,
            path,
          }) as string;
        } else if (_issue.format === 'includes') {
          return `Invalid string: must include "${_issue.includes}"`;
        } else if (_issue.format === 'regex') {
          message = tError('errors.invalid_string.regex', {
            pattern: _issue.pattern,
            path,
          }) as string;
        } else {
          message = tError(key, {
            validation,
            path,
          }) as string;
        }

        break;
      }

      case 'unrecognized_keys': {
        message = tError('errors.unrecognized_keys', {
          keys: joinValues(issue.keys, ', '),
          path,
        });
        break;
      }
      case 'invalid_key': {
        message = tError('errors.invalid_key', {
          origin: issue.origin,
          path,
        });
        break;
      }
      case 'invalid_union': {
        message = tError('errors.invalid_union');
        break;
      }
      case 'invalid_element': {
        message = tError('errors.invalid_element', {
          origin: issue.origin,
        }) as string;
        break;
      }
    }

    return message;
  };
};

export function translatedZodError(option: ZodI18nMapOption): {
  localeError: $ZodErrorMap;
} {
  return {
    localeError: error(option),
  };
}
